import React, { useState, useEffect, useCallback } from 'react';
import { adminService } from '../services/api';
import './QuotationsManagement.css';

const COP = (n) =>
  new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(Number(n) || 0);

const emptyLine = () => ({
  line_mode: 'item',
  description: '',
  quantity: 1,
  unit_price_cents: 0,
  line_total_cents: 0,
  phase: ''
});

const QuotationsManagement = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [uploadLink, setUploadLink] = useState(null);

  const loadList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminService.listQuotations();
      setList(res.quotations || []);
    } catch (e) {
      setError(e.response?.data?.message || 'No se pudo cargar');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const openNew = () => {
    setSelected(null);
    setAttachments([]);
    setUploadLink(null);
    setForm({
      category: 'otros',
      client_name: '',
      client_email: '',
      client_phone: '',
      project_title: '',
      scope_summary: '',
      validity_until: '',
      notes_client: '',
      notes_internal: '',
      show_growth_phases: false,
      status: 'draft',
      lines: [emptyLine()]
    });
  };

  const loadDetail = async (id) => {
    setMessage(null);
    setUploadLink(null);
    try {
      const res = await adminService.getQuotation(id);
      const d = res.data;
      setSelected(d.quotation);
      setAttachments(d.attachments || []);
      const lines = (d.lines || []).map((ln) => ({
        line_mode: ln.line_mode || 'item',
        description: ln.description || '',
        quantity: ln.quantity != null ? Number(ln.quantity) : 1,
        unit_price_cents: Number(ln.unit_price_cents) || 0,
        line_total_cents: Number(ln.line_total_cents) || 0,
        phase: ln.phase != null ? String(ln.phase) : ''
      }));
      setForm({
        category: d.quotation.category,
        client_name: d.quotation.client_name,
        client_email: d.quotation.client_email,
        client_phone: d.quotation.client_phone || '',
        project_title: d.quotation.project_title,
        scope_summary: d.quotation.scope_summary || '',
        validity_until: d.quotation.validity_until || '',
        notes_client: d.quotation.notes_client || '',
        notes_internal: d.quotation.notes_internal || '',
        show_growth_phases: !!d.quotation.show_growth_phases,
        status: d.quotation.status,
        lines: lines.length ? lines : [emptyLine()]
      });
    } catch (e) {
      setError(e.response?.data?.message || 'Error al abrir');
    }
  };

  const handleSave = async () => {
    if (!form) return;
    setSaving(true);
    setMessage(null);
    try {
      const payload = {
        ...form,
        lines: form.lines.map((ln) => ({
          line_mode: ln.line_mode,
          description: ln.description,
          quantity: ln.line_mode === 'item' ? Number(ln.quantity) || 0 : null,
          unit_price_cents: ln.line_mode === 'item' ? Number(ln.unit_price_cents) || 0 : null,
          line_total_cents: Number(ln.line_total_cents) || 0,
          phase: ln.phase === '' || ln.phase == null ? null : parseInt(ln.phase, 10)
        }))
      };
      if (!selected) {
        const res = await adminService.createQuotation(payload);
        setMessage('Cotización creada');
        await loadList();
        await loadDetail(res.data.quotation.id);
      } else {
        await adminService.updateQuotation(selected.id, payload);
        setMessage('Guardado');
        await loadList();
        await loadDetail(selected.id);
      }
    } catch (e) {
      setError(e.response?.data?.message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handlePdf = async () => {
    if (!selected) return;
    try {
      const blob = await adminService.downloadQuotationPdfBlob(selected.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selected.public_number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.response?.data?.message || 'Error generando PDF');
    }
  };

  const handleSend = async () => {
    if (!selected) return;
    if (!window.confirm('¿Enviar cotización por correo al cliente y copia BCC a ventas@javiland.com?')) return;
    try {
      await adminService.sendQuotation(selected.id);
      setMessage('Correo enviado');
      await loadDetail(selected.id);
      await loadList();
    } catch (e) {
      setError(e.response?.data?.message || 'Error al enviar');
    }
  };

  const handleNewVersion = async () => {
    if (!selected) return;
    try {
      const res = await adminService.newQuotationVersion(selected.id);
      setMessage('Nueva versión: ' + res.data.quotation.public_number);
      await loadList();
      await loadDetail(res.data.quotation.id);
    } catch (e) {
      setError(e.response?.data?.message || 'Error');
    }
  };

  const handleUploadToken = async () => {
    if (!selected) return;
    try {
      const res = await adminService.createQuotationUploadToken(selected.id);
      setUploadLink(res.data);
      setMessage('Enlace de adjuntos listo (copia la URL)');
    } catch (e) {
      setError(e.response?.data?.message || 'Error');
    }
  };

  const deleteAttachment = async (attId) => {
    if (!selected) return;
    if (!window.confirm('¿Eliminar adjunto del servidor?')) return;
    try {
      await adminService.deleteQuotationAttachment(selected.id, attId);
      await loadDetail(selected.id);
    } catch (e) {
      setError(e.response?.data?.message || 'Error');
    }
  };

  const updateLine = (idx, field, val) => {
    setForm((f) => {
      const lines = [...f.lines];
      lines[idx] = { ...lines[idx], [field]: val };
      if (field === 'line_mode' && val === 'text_total') {
        lines[idx].quantity = null;
        lines[idx].unit_price_cents = null;
      }
      if (field === 'quantity' || field === 'unit_price_cents') {
        const q = Number(lines[idx].quantity) || 0;
        const u = Number(lines[idx].unit_price_cents) || 0;
        if (lines[idx].line_mode === 'item') {
          lines[idx].line_total_cents = Math.round(q * u);
        }
      }
      return { ...f, lines };
    });
  };

  const totalPreview = form
    ? form.lines.reduce((s, ln) => s + (Number(ln.line_total_cents) || 0), 0)
    : 0;

  return (
    <div className="quotations-admin">
      <div className="quotations-admin-head">
        <div className="quotations-admin-head-text">
          <h1>Cotizaciones</h1>
          <p className="quotations-admin-intro">
            PDF corporativo · envío por correo (cliente + BCC ventas@javiland.com) · numeración COT-AÑO-NNN
          </p>
        </div>
        <button type="button" className="quotations-btn-main" onClick={openNew}>
          Nueva cotización
        </button>
      </div>

      {error && (
        <div className="quotations-alert quotations-alert-err" role="alert">
          {error}
          <button type="button" className="quotations-alert-x" onClick={() => setError(null)}>×</button>
        </div>
      )}
      {message && (
        <div className="quotations-alert quotations-alert-ok">
          {message}
          <button type="button" className="quotations-alert-x" onClick={() => setMessage(null)}>×</button>
        </div>
      )}

      <div className="quotations-admin-grid">
        <aside className="quotations-list-panel">
          <h2>Listado</h2>
          {loading ? (
            <p>Cargando…</p>
          ) : (
            <ul className="quotations-list">
              {list.map((q) => (
                <li key={q.id}>
                  <button
                    type="button"
                    className={selected?.id === q.id ? 'is-active' : ''}
                    onClick={() => loadDetail(q.id)}
                  >
                    <strong>{q.public_number}</strong>
                    <span>{q.project_title}</span>
                    <small>{q.status} · v{q.version}</small>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        <section className="quotations-form-panel">
          {!form ? (
            <p className="quotations-placeholder">Selecciona una cotización o crea una nueva.</p>
          ) : (
            <>
              {selected && (
                <p className="quotations-meta">
                  <strong>{selected.public_number}</strong> · versión {selected.version}
                  {selected.sent_at ? ` · enviada ${selected.sent_at}` : ''}
                </p>
              )}

              <div className="quotations-form-section">
                <h3 className="quotations-form-section-title">Clasificación</h3>
                <div className="quotations-form-row quotations-form-row-2">
                  <label>
                    Categoría (interna)
                    <select
                      value={form.category}
                      onChange={(e) => setForm({ ...form, category: e.target.value })}
                    >
                      <option value="otros">Otros</option>
                      <option value="obra">Obra / remodelación</option>
                      <option value="growth_partner">Growth Partner</option>
                      <option value="bienestar">Bienestar</option>
                    </select>
                  </label>
                  {selected && (
                    <label>
                      Estado
                      <select
                        value={form.status}
                        onChange={(e) => setForm({ ...form, status: e.target.value })}
                      >
                        <option value="draft">Borrador</option>
                        <option value="sent">Enviada</option>
                        <option value="accepted">Aceptada</option>
                        <option value="rejected">Rechazada</option>
                        <option value="expired">Vencida</option>
                      </select>
                    </label>
                  )}
                </div>
              </div>

              <div className="quotations-form-section">
                <h3 className="quotations-form-section-title">Cliente</h3>
                <div className="quotations-form-row quotations-form-row-3">
                  <label>
                    Cliente
                    <input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} />
                  </label>
                  <label>
                    Correo
                    <input type="email" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} placeholder="requerido para guardar" />
                  </label>
                  <label>
                    Teléfono
                    <input value={form.client_phone} onChange={(e) => setForm({ ...form, client_phone: e.target.value })} />
                  </label>
                </div>
              </div>

              <div className="quotations-form-section">
                <h3 className="quotations-form-section-title">Proyecto</h3>
                <label>
                  Proyecto / título
                  <input value={form.project_title} onChange={(e) => setForm({ ...form, project_title: e.target.value })} />
                </label>
                <label>
                  Resumen de alcance (PDF)
                  <textarea rows={4} value={form.scope_summary} onChange={(e) => setForm({ ...form, scope_summary: e.target.value })} />
                </label>
                <div className="quotations-form-row quotations-form-row-2">
                  <label>
                    Vigencia hasta
                    <input type="date" value={form.validity_until || ''} onChange={(e) => setForm({ ...form, validity_until: e.target.value })} />
                  </label>
                  <label className="quotations-check">
                    <input
                      type="checkbox"
                      checked={form.show_growth_phases}
                      onChange={(e) => setForm({ ...form, show_growth_phases: e.target.checked })}
                    />
                    PDF por fases 1–3
                  </label>
                </div>
              </div>

              <div className="quotations-form-section">
                <h3 className="quotations-form-section-title">Notas</h3>
                <div className="quotations-form-row quotations-form-row-2">
                  <label>
                    Notas al cliente (PDF)
                    <textarea rows={3} value={form.notes_client} onChange={(e) => setForm({ ...form, notes_client: e.target.value })} />
                  </label>
                  <label>
                    Notas internas (no PDF)
                    <textarea rows={3} value={form.notes_internal} onChange={(e) => setForm({ ...form, notes_internal: e.target.value })} />
                  </label>
                </div>
              </div>

              <div className="quotations-form-section">
                <h3 className="quotations-form-section-title">Líneas de cotización</h3>
                <p className="quotations-hint">Ítem: cantidad × valor unitario en COP (ej. 300000). Texto+total: solo descripción y total.</p>
                <div className="quotations-lines-wrap">
              {form.lines.map((ln, idx) => (
                <div key={idx} className="quotations-line-card">
                  <div className="quotations-line-card-header">
                    <label>
                      Modo
                      <select
                        value={ln.line_mode}
                        onChange={(e) => updateLine(idx, 'line_mode', e.target.value)}
                      >
                        <option value="item">Ítem</option>
                        <option value="text_total">Texto + total</option>
                      </select>
                    </label>
                    {form.show_growth_phases && (
                      <label>
                        Fase
                        <select value={ln.phase} onChange={(e) => updateLine(idx, 'phase', e.target.value)}>
                          <option value="">—</option>
                          <option value="1">1</option>
                          <option value="2">2</option>
                          <option value="3">3</option>
                        </select>
                      </label>
                    )}
                  </div>
                  <label className="quotations-line-desc">
                    Descripción
                    <input value={ln.description} onChange={(e) => updateLine(idx, 'description', e.target.value)} />
                  </label>
                  {ln.line_mode === 'item' ? (
                    <div className="quotations-form-row">
                      <label>
                        Cantidad
                        <input
                          type="number"
                          step="0.01"
                          value={ln.quantity}
                          onChange={(e) => updateLine(idx, 'quantity', e.target.value)}
                        />
                      </label>
                      <label>
                        Valor unitario (COP)
                        <input
                          type="number"
                          value={ln.unit_price_cents}
                          onChange={(e) => updateLine(idx, 'unit_price_cents', e.target.value)}
                          placeholder="300000"
                        />
                      </label>
                      <span className="quotations-line-total">Subtotal: {COP(ln.line_total_cents)}</span>
                    </div>
                  ) : (
                    <label>
                      Total línea (COP)
                      <input
                        type="number"
                        value={ln.line_total_cents}
                        onChange={(e) => updateLine(idx, 'line_total_cents', e.target.value)}
                      />
                    </label>
                  )}
                  <div className="quotations-line-card-footer">
                    <button
                      type="button"
                      className="quotations-btn-ghost"
                      onClick={() =>
                        setForm((f) => ({ ...f, lines: f.lines.filter((_, i) => i !== idx) }))
                      }
                    >
                      Quitar línea
                    </button>
                  </div>
                </div>
              ))}
                </div>
              <button
                type="button"
                className="quotations-btn-ghost"
                onClick={() => setForm((f) => ({ ...f, lines: [...f.lines, emptyLine()] }))}
              >
                + Añadir línea
              </button>

              <div className="quotations-total-bar">
                <span><strong>Total cotización (preview)</strong></span>
                <span>{COP(totalPreview)}</span>
              </div>
              </div>

              <div className="quotations-actions">
                <button type="button" className="quotations-btn-main" onClick={handleSave} disabled={saving}>
                  {saving ? 'Guardando…' : 'Guardar'}
                </button>
                {selected && (
                  <>
                    <button type="button" className="quotations-btn-secondary" onClick={handlePdf}>
                      Descargar PDF
                    </button>
                    <button type="button" className="quotations-btn-secondary" onClick={handleSend}>
                      Enviar por correo
                    </button>
                    <button type="button" className="quotations-btn-secondary" onClick={handleNewVersion}>
                      Nueva versión
                    </button>
                    <button type="button" className="quotations-btn-secondary" onClick={handleUploadToken}>
                      Enlace adjuntos (móvil)
                    </button>
                  </>
                )}
              </div>

              {uploadLink && (
                <div className="quotations-upload-box">
                  <strong>Subir archivos desde el móvil</strong>
                  <input readOnly value={uploadLink.upload_page_url} onFocus={(e) => e.target.select()} />
                  <small>Vence: {uploadLink.expires_at}</small>
                </div>
              )}

              {selected && attachments.length > 0 && (
                <div className="quotations-attachments">
                  <h4>Adjuntos</h4>
                  <ul>
                    {attachments.map((a) => (
                      <li key={a.id}>
                        {a.original_name} · {(a.size_bytes / 1024).toFixed(1)} KB · {a.created_at}
                        <button type="button" onClick={() => deleteAttachment(a.id)}>Eliminar</button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </section>
      </div>
    </div>
  );
};

export default QuotationsManagement;
